/**
 * Container Validation Schemas
 *
 * Zod schemas for container-related forms and data validation.
 * Matches backend validation rules for consistency.
 */

import { z } from 'zod';

/**
 * Container size enum
 */
export const containerSizeEnum = z.enum(['10Y', '20Y', '30Y', '40Y']);

/**
 * Container type enum
 */
export const containerTypeEnum = z.enum(['rolloff', 'compactor', 'stationary']);

/**
 * Container condition enum
 */
export const containerConditionEnum = z.enum([
  'excellent',
  'good',
  'fair',
  'poor',
  'needs_repair',
]);

/**
 * Container status enum
 */
export const containerStatusEnum = z.enum([
  'available',
  'deployed',
  'in_transit',
  'maintenance',
  'retired',
]);

/**
 * Location type enum
 */
export const locationTypeEnum = z.enum(['yard', 'job_site', 'in_transit']);

/**
 * Container location schema
 */
export const containerLocationSchema = z.object({
  type: locationTypeEnum,
  address: z.string().min(1, 'Location address is required'),
  yardId: z.string().uuid().optional(),
});

/**
 * Create Container Schema
 * Used for creating new containers
 */
export const createContainerSchema = z.object({
  containerNumber: z
    .string()
    .min(1, 'Container number is required')
    .max(50, 'Container number must be less than 50 characters'),
  size: containerSizeEnum,
  type: containerTypeEnum,
  condition: containerConditionEnum,
  location: containerLocationSchema,
  purchaseDate: z.string().optional(),
});

export type CreateContainerDTO = z.infer<typeof createContainerSchema>;

/**
 * Assign Container Schema
 * Used for assigning containers to jobs
 */
export const assignContainerSchema = z.object({
  jobId: z.string().uuid('Invalid job ID'),
  jobAddress: z
    .string()
    .min(1, 'Job address is required')
    .max(500, 'Job address must be less than 500 characters'),
  deploymentDate: z.string().datetime('Invalid deployment date'),
});

export type AssignContainerDTO = z.infer<typeof assignContainerSchema>;

/**
 * Retrieve Container Schema
 * Used for retrieving containers from jobs
 */
export const retrieveContainerSchema = z.object({
  retrievalDate: z.string().datetime('Invalid retrieval date'),
  finalWeight: z.number().positive('Weight must be positive').optional(),
  notes: z.string().max(1000, 'Notes must be less than 1000 characters').optional(),
});

export type RetrieveContainerDTO = z.infer<typeof retrieveContainerSchema>;

/**
 * Return to Yard Schema
 * Used for returning containers to the yard
 */
export const returnToYardSchema = z.object({
  yardAddress: z
    .string()
    .min(1, 'Yard address is required')
    .max(500, 'Yard address must be less than 500 characters'),
  returnDate: z.string().datetime('Invalid return date'),
});

export type ReturnToYardDTO = z.infer<typeof returnToYardSchema>;

/**
 * Mark for Maintenance Schema
 * Used for marking containers for maintenance
 */
export const markForMaintenanceSchema = z.object({
  reason: z
    .string()
    .min(1, 'Maintenance reason is required')
    .max(1000, 'Reason must be less than 1000 characters'),
  scheduledDate: z.string().datetime('Invalid scheduled date').optional(),
});

export type MarkForMaintenanceDTO = z.infer<typeof markForMaintenanceSchema>;

/**
 * Container filter schema
 * Used for filtering container list
 */
export const containerFilterSchema = z.object({
  size: containerSizeEnum.optional(),
  status: containerStatusEnum.optional(),
  availableOnly: z.boolean().optional(),
  yardId: z.string().optional(),
});

export type ContainerFilterDTO = z.infer<typeof containerFilterSchema>;
